import { JourneyDayTripItem } from "../../models/axiosModel"

/** Day-trip catalog type ids from journey-details (Transfers / Tours). */
export const DAY_TRIP_TYPE_TRANSFER = 1
export const DAY_TRIP_TYPE_TOUR = 2

export type TripKindFromType = "tour" | "transfer"

/** Resolve tour vs transfer from journey-details `day_trip_type_id` / name. */
export const tripKindFromDayTripType = (
  trip: Pick<JourneyDayTripItem, "day_trip_type_id" | "day_trip_type_name"> & {
    _trip_kind?: string
  },
): TripKindFromType | undefined => {
  const saved = String(trip._trip_kind || "").toLowerCase()
  if (saved === "tour" || saved === "transfer") return saved

  const typeId = Number(trip.day_trip_type_id)
  if (typeId === DAY_TRIP_TYPE_TRANSFER) return "transfer"
  if (typeId === DAY_TRIP_TYPE_TOUR) return "tour"

  const typeName = String(trip.day_trip_type_name || "").trim().toLowerCase()
  if (typeName.includes("transfer")) return "transfer"
  if (typeName.includes("tour")) return "tour"
  return undefined
}

const isBlankPrice = (raw: unknown): boolean =>
  raw === null || raw === undefined || raw === ""

/** True when journey-details (or prior enrich) already has a normal seating unit price. */
export const hasTripSeatingPriceSnapshot = (trip: JourneyDayTripItem): boolean => {
  if (!isBlankPrice((trip as any).seating_price)) return true
  if (!isBlankPrice((trip as any)._seating_price)) return true
  return false
}

/** True when DT unit price is present on the trip (`0` counts as present). */
export const hasTripDinnerTransportPriceSnapshot = (trip: JourneyDayTripItem): boolean =>
  !isBlankPrice((trip as any).dinner_transport_price)

/**
 * Map journey-details seating / DT snapshot fields onto FE underscore fields used by pricing.
 * Does not invent prices when BE omitted them (old quotations).
 */
export const applyTripSeatingSnapshotFromApi = (trip: JourneyDayTripItem): JourneyDayTripItem => {
  const kind = tripKindFromDayTripType(trip)
  const next: JourneyDayTripItem = { ...trip }

  if (kind) next._trip_kind = kind

  if (typeof trip.flag_per_person === "boolean") {
    next._seating_is_per_person = trip.flag_per_person
  }

  if (typeof trip.flag_enable_dinner === "boolean") {
    next._flag_enable_dinner = trip.flag_enable_dinner
  }

  if (!isBlankPrice(trip.seating_price)) {
    next._seating_price = Number(trip.seating_price)
  }

  const seatingName = String(trip.seating_type_name || "").trim().toUpperCase()
  const isSic = seatingName === "SIC" || String((trip as any)._seating_value || "").toUpperCase() === "SIC"
  if ((trip as any)._seating_value == null || (trip as any)._seating_value === "") {
    if (isSic) {
      const sid = trip.seating_type_id
      next._seating_value = sid != null && Number(sid) !== 0 ? String(sid) : "SIC"
    } else if (trip.seating_type_id != null && Number(trip.seating_type_id) !== 0) {
      next._seating_value = String(trip.seating_type_id)
    }
  }

  return next
}

export const applyTripsSeatingSnapshotFromApi = (
  trips: JourneyDayTripItem[] | undefined,
): JourneyDayTripItem[] => (trips || []).map((t) => applyTripSeatingSnapshotFromApi(t))

/** Normal transport total from snapshot / `_seating_price` (vehicle or × pax). */
export const getTripNormalTransportFromSnapshot = (
  trip: JourneyDayTripItem,
  paxCount: number,
): number => {
  const base = Number((trip as any)._seating_price ?? trip.seating_price ?? 0)
  const perPerson =
    typeof (trip as any)._seating_is_per_person === "boolean"
      ? Boolean((trip as any)._seating_is_per_person)
      : typeof trip.flag_per_person === "boolean"
        ? trip.flag_per_person
        : String(trip.seating_type_name || "").trim().toUpperCase() === "SIC"
  return perPerson ? base * Math.max(0, Number(paxCount) || 0) : base
}

export type SnapshotDinnerTransportInfo = {
  unitPrice: number
  totalPrice: number
  seatingTypeName: string
  perPerson: boolean
}

const emptyDinner: SnapshotDinnerTransportInfo = {
  unitPrice: 0,
  totalPrice: 0,
  seatingTypeName: "",
  perPerson: false,
}

/**
 * DT amount from trip-level `dinner_transport_price` when options seating row is unavailable.
 * Caller must already know DT applies (day DT + enable dinner + not dinner-included).
 */
export const resolveDinnerTransportFromTripSnapshot = (
  trip: JourneyDayTripItem,
  paxCount: number,
): SnapshotDinnerTransportInfo => {
  if (!hasTripDinnerTransportPriceSnapshot(trip)) return emptyDinner

  const unitPrice = Number((trip as any).dinner_transport_price)
  const perPerson =
    typeof (trip as any)._seating_is_per_person === "boolean"
      ? Boolean((trip as any)._seating_is_per_person)
      : typeof trip.flag_per_person === "boolean"
        ? trip.flag_per_person
        : String(trip.seating_type_name || "").trim().toUpperCase() === "SIC"
  const pax = Math.max(0, Number(paxCount) || 0)
  return {
    unitPrice,
    totalPrice: perPerson ? unitPrice * pax : unitPrice,
    seatingTypeName: String(trip.seating_type_name || ""),
    perPerson,
  }
}

/** Cache / network gate: skip fetch when this quotation+city+date was already loaded from options API. */
export const shouldFetchJourneyOptionsForKey = (
  loadedKeys: Set<string>,
  dedupeKey: string,
): boolean => !loadedKeys.has(dedupeKey)

export const buildJourneyOptionsDedupeKey = (
  quotationId: string | null | undefined,
  cityDateKey: string,
): string => `${String(quotationId ?? "new")}|${cityDateKey}`

/** True when any trip on the day is missing a seating price snapshot (old quote / pre-BE). */
export const dayTripsMissingSeatingPriceSnapshot = (
  trips: JourneyDayTripItem[] | undefined,
): boolean => (trips || []).some((t) => Boolean(t.day_trip_id) && !hasTripSeatingPriceSnapshot(t))

type SeatingSnapshotSource = {
  price?: number | null
  flag_per_person?: boolean
  dinner_transport_price?: number | null
}

/**
 * Persist journey-details seating/DT snapshot fields from the selected options seating row
 * (and trip-level flag_enable_dinner), so PUT can round-trip them once BE accepts the fields.
 */
export const buildPersistedSeatingSnapshotFields = (
  seating: SeatingSnapshotSource | undefined,
  tripFlagEnableDinner: boolean,
  overrides?: { seatingPrice?: number; perPerson?: boolean },
): Pick<
  JourneyDayTripItem,
  | "seating_price"
  | "flag_per_person"
  | "flag_enable_dinner"
  | "dinner_transport_price"
  | "_seating_price"
  | "_seating_is_per_person"
  | "_flag_enable_dinner"
> => {
  const seatingPrice = Number(
    overrides?.seatingPrice ?? seating?.price ?? 0,
  )
  const perPerson =
    typeof overrides?.perPerson === "boolean"
      ? overrides.perPerson
      : Boolean(seating?.flag_per_person)
  const dinnerRaw = seating?.dinner_transport_price
  const dinnerTransportPrice = isBlankPrice(dinnerRaw) ? null : Number(dinnerRaw)
  return {
    seating_price: seatingPrice,
    flag_per_person: perPerson,
    flag_enable_dinner: tripFlagEnableDinner,
    dinner_transport_price: dinnerTransportPrice,
    _seating_price: seatingPrice,
    _seating_is_per_person: perPerson,
    _flag_enable_dinner: tripFlagEnableDinner,
  }
}
